import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { on_mobile } from "../../utils/utils";
import { RawIcon, gen_line } from "./TimelineCommon";
import Register from "../../assets/icons/Timeline/Desktop/Icons/1Register.png";
import KitDispatched from "../../assets/icons/Timeline/Desktop/Icons/3KitDispatched.png";
import ActivateKit from "../../assets/icons/Timeline/Desktop/Icons/4CollectSample.png";
import LabAnalysis from "../../assets/icons/Timeline/Desktop/Icons/6SampleReceived.png";
import Certificate from "../../assets/icons/Timeline/Desktop/Icons/7ResultsReady.png";
import PropTypes from "prop-types";
import { Alert } from "react-bootstrap";
import { Button } from "../../stories/Buttons/Button";
import { useMediaQuery } from "react-responsive";

/**
 * Generates the steps in the wellness timeline based on the test status.
 * Each step is defined with its label, matching condition, graphics, and instructions.
 * 
 * @returns {Array} - List of steps, each containing information like label, match function, and instructions.
 */

const get_steps = () => {
  const all_tests = [
    {
      label: "Test Purchased",
      match: status => status === "activating",
      graphic: Register,
      pos: "a",
      instructions: () => "Your test has been purchased, we'll dispatch your kit soon",
    },
    {
      label: "Kit Dispatched",
      label_before: "Awaiting Dispatch",
      match: status => status === "dispatched",
      graphic: KitDispatched,
      pos: "b",
      instructions: () =>
        "The next step is for us to post your collection kit, it should be shipped within 2 business days."
    },
    {
      label: "Sample Collected & Kit Activated",
      label_before: "Collect Sample & Activate Kit",
      match: status => status === "activated" || status === "quarantine-cleared",
      graphic: ActivateKit,
      pos: "a",
      link: "/tests/wellness/resume-activation/{test_id}/",
      instructions: ({ test_id }) => (
        <>
          <p>Your kit is in the post and should be with you soon.</p>
          <p>
            As soon as you receive your kit, you should open it, read the
            instructions and activate it, before returning it to us.
          </p>
          <Link
            to={`/tests/wellness/resume-activation/${test_id}/`}
            className="btn btn-primary"
          >
            Activate Test Kit
          </Link>
        </>
      )
    },
    {
      label: "Sample Received",
      label_before: "Awaiting Sample Return",
      match: status =>
        status === "ready-for-processing" ||
        status === "processing" ||
        status === "error" ||
        status === "rejected",
      failed: status => status === "rejected" || status === "error",
      graphic: KitDispatched,
      pos: "b",
      instructions: () => (
        <p>
          Your kit has been activated, please make sure you post it back to us
          as soon as possible.
        </p>
      )
    },
    {
      label: "Lab Analysis",
      match: () => false,
      graphic: LabAnalysis,
      pos: "b",
      instructions: () =>
        "Your sample has been received and is being processed."
    },
    {
      label: "Results Ready",
      label_before: "Get Results",
      match: status => status === "published" || status === "results-ready",
      graphic: Certificate,
      pos: "a"
    }
  ];
  return all_tests.filter(s => s);
};

const Icon = ({ 
  step, 
  step_index, 
  currentStepIndex, 
  any_failed, 
  test_id, 
  id, 
  status, 
  navigate, 
  step_increment 
}) => {
  const failed = step.failed?.(status);
  const current = any_failed ? false : step_index === currentStepIndex + 1;
  const clickLink = current && step.link?.replace("{test_id}", test_id || id);
  
  return (
    <RawIcon
      step={step}
      onClick={clickLink ? () => navigate(clickLink) : null}
      step_index={step_index}
      complete={step_index <= currentStepIndex}
      failed={failed}
      current={current}
      step_increment={step_increment}
    />
  );
};

Icon.propTypes = {
  step: PropTypes.shape({
    label: PropTypes.string,
    match: PropTypes.func,
    graphic: PropTypes.string,
    pos: PropTypes.string,
    instructions: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    link: PropTypes.string,
    failed: PropTypes.func,
  }).isRequired,
  step_index: PropTypes.number.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  any_failed: PropTypes.bool.isRequired,
  test_id: PropTypes.string,
  id: PropTypes.string,
  status: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  step_increment: PropTypes.number.isRequired
};

const WellnessTimeline = ({ test }) => {
  let {
    test_id,
    test_kit_status: status,
    report_id,
    id,
  } = test;

  const navigate = useNavigate();
  const steps = get_steps();
  const isMobile = on_mobile();
  const step_increment = isMobile ? 60 : 160;
  let any_failed = false;
  const isMobileDevice = useMediaQuery({maxWidth: 840})

  if (status === "in-progress") {
    status = "dispatched";
  }

  const results_ready =
    status === "published" || status === "results-ready";

  let report_link = `/tests`;

  if (report_id !== null && report_id !== undefined) {
    report_link = `/wellness-report/${report_id}`;
  }

  const currentStepIndex = steps.findIndex(step =>
    step.match(status)
  );

  // Check if any steps have failed
  steps.forEach(step => {
    if (step.failed?.(status)) {
      any_failed = true;
    }
  });

  const next_step = steps[currentStepIndex + 1] || { title: () => null };
  const Instructions = next_step.instructions || (() => null);

  const line_points = (steps || []).length > 0
    ? gen_line(steps, step_increment).map(xy => xy.join(",")).join(" ")
    : [];

  if (results_ready) {
       return (
        <Alert
          className="mt-3 custom-width-for-assessment row ms-0"
          variant="dark"
          fade={false}
        >
          <span className="col-md-8">
            <strong>
              <Link
                to={report_link}
                className="alert-link"
              >
                View your result
              </Link>
            </strong>
            <p className="text-muted ageo-medium">
              {" "}
              Our doctor has reviewed your results. Your report has been published
              and is ready to view.
            </p>
          </span>
          <div className={`col-md-4 d-flex justify-content-end align-items-center ${isMobile ? "flex-column justify-content-center gap-3" : "justify-content-end"}`}>
            <Button
              className={`pt-2 btn-green justify-content-end ${isMobile && "w-100"}`}
              href={report_link}
              color="primary"
              label="View report"
            />
          </div>
      </Alert>
    );
  }

  return (
    <div className="my-3 border-0">
      <div className="text-md-left text-muted">
        {results_ready && (
          <>
            <p>Your results are ready</p>
            <Link to={report_link}>
              <Button variant="primary">View report</Button>
            </Link>
          </>
        )}
       {test.test_kit_status !== "error" && <Instructions test_id={test_id || id} /> }
      </div>
      {/* <svg
        className="progress-svg"
        width="100%"
        viewBox={isMobile ? "0 0 300 400" : "0 0 900 300"}
      >
        <polyline points={line_points} />
        {steps.map((step, i) => (
          <Icon 
            key={step.id} 
            step={step} 
            step_index={i}
            currentStepIndex={currentStepIndex}
            any_failed={any_failed}
            test_id={test_id}
            id={id}
            status={status}
            navigate={navigate}
            step_increment={step_increment}
          />
        ))}
      </svg> */}
    </div>
  );
};

WellnessTimeline.propTypes = {
  test: PropTypes.shape({
    test_id: PropTypes.string,
    test_kit_status: PropTypes.string,
    report_id: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  step: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};

export default WellnessTimeline;
